import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Chatbot from '../components/Chatbot';
import Footer from '../components/Footer';

export default function SecurityPage() {
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isPublic={true} />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
        <article className="prose prose-lg max-w-none">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 mt-0">Security</h1>
          
          <div className="text-sm text-gray-600 mb-8">
            Last Updated: October 9, 2025
          </div>
          
          <div className="p-4 bg-blue-50 border-l-4 border-blue-500 text-blue-900 mb-8">
            <p className="font-semibold">
              At GetFlowDocs, we take the security of your data seriously. This page outlines our security practices 
              and commitment to protecting your Salesforce data and personal information.
            </p>
          </div>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Infrastructure Security</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Hosting and Infrastructure</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Application Hosting:</strong> Vercel with enterprise-grade security</li>
              <li><strong>Database:</strong> Supabase with PostgreSQL, SOC 2 Type II certified</li>
              <li><strong>Authentication:</strong> Supabase Auth with secure session management</li>
              <li><strong>CDN:</strong> Global CDN with DDoS protection</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Data Encryption</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>In Transit:</strong> All connections secured with TLS encryption</li>
              <li><strong>At Rest:</strong> Database protected with AES-256 encryption via Supabase</li>
              <li><strong>Secure Infrastructure:</strong> SOC 2 certified database platform</li>
              <li><strong>OAuth Tokens:</strong> Refresh tokens encrypted in database (AES-256), access tokens generated fresh and cached in memory only</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Application Security</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Authentication and Authorization</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>OAuth 2.0 with PKCE:</strong> Secure Salesforce authentication with proof key exchange</li>
              <li><strong>Secure Sessions:</strong> JWT-based authentication via Supabase Auth</li>
              <li><strong>Password Protection:</strong> Minimum password length requirements</li>
              <li><strong>Role-Based Access:</strong> Account owner and team member permissions</li>
              <li><strong>Enterprise Auth:</strong> Supabase Auth with SSO capabilities</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Salesforce Integration</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Metadata-Only Access:</strong> FlowDocs accesses ONLY Flow definitions, Flow Interview logs, and basic User metadata - never your business records or customer data</li>
              <li><strong>Read-Only Operations:</strong> All API calls are SELECT queries - no INSERT, UPDATE, or DELETE operations ever performed</li>
              <li><strong>No Record Data Access:</strong> We never query Accounts, Contacts, Opportunities, or any custom objects containing your business data</li>
              <li><strong>Token Management:</strong> Refresh tokens encrypted at rest, access tokens cached in memory only (never persisted)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Protection</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Data Handling</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Minimal Data Collection:</strong> Only what's necessary</li>
              <li><strong>Data Isolation:</strong> Tenant isolation in multi-tenant architecture</li>
              <li><strong>Secure Deletion:</strong> Cryptographic erasure</li>
              <li><strong>Data Portability:</strong> Export your data anytime</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">AI Processing</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Claude AI Integration:</strong>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>No customer data used for training</li>
                  <li>Processing only, no permanent storage</li>
                  <li>Anthropic's enterprise security standards</li>
                  <li>Data deleted after processing</li>
                </ul>
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Operational Security</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Access Controls</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Principle of Least Privilege:</strong> Minimal access rights</li>
              <li><strong>Secure Authentication:</strong> Supabase Auth with session management</li>
              <li><strong>Row Level Security:</strong> Database-level access controls</li>
              <li><strong>Audit Logging:</strong> Comprehensive security event tracking</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Monitoring and Protection</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Infrastructure Monitoring:</strong> Provider-level 24/7 security monitoring (Vercel, Supabase, Digital Ocean)</li>
              <li><strong>DDoS Protection:</strong> Global CDN with automatic threat mitigation</li>
              <li><strong>Security Event Logging:</strong> Critical authentication and authorization events tracked (logins, org connections, access failures)</li>
              <li><strong>Database Audit Logs:</strong> Supabase provides comprehensive database activity logging</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Development Security</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Secure SDLC:</strong> Security integrated into development process</li>
              <li><strong>Code Reviews:</strong> Security-focused code review before deployment</li>
              <li><strong>TypeScript:</strong> Type safety to prevent common security vulnerabilities</li>
              <li><strong>Dependency Management:</strong> Regular updates to patch known vulnerabilities</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Security Standards and Infrastructure</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Enterprise-Grade Infrastructure</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Supabase Database:</strong> SOC 2 Type II certified infrastructure</li>
              <li><strong>Vercel Hosting:</strong> Enterprise security with SOC 2 compliance</li>
              <li><strong>Anthropic Claude AI:</strong> Enterprise-grade AI with no training on customer data</li>
              <li><strong>Digital Ocean:</strong> ISO 27001 certified cloud infrastructure</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Security Practices</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>OWASP Top 10:</strong> Protection against common vulnerabilities</li>
              <li><strong>Privacy by Design:</strong> Minimal data collection and retention</li>
              <li><strong>Dependency Management:</strong> Regular security updates and patches</li>
              <li><strong>Code Reviews:</strong> All changes reviewed before deployment</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Security Features for Users</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Security</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Password Requirements:</strong> Minimum length enforcement</li>
              <li><strong>Secure Authentication:</strong> Supabase Auth with email verification</li>
              <li><strong>Login Tracking:</strong> Security event logging for all authentication attempts</li>
              <li><strong>Account Recovery:</strong> Email-based password reset process</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Data Security</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Secure Data Access:</strong> All connections protected by HTTPS encryption</li>
              <li><strong>API Security:</strong> Token-based authentication for all API requests</li>
              <li><strong>Database Access Controls:</strong> Row Level Security (RLS) ensures data isolation</li>
              <li><strong>Audit Trails:</strong> Security event logging for authentication and authorization</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Shared Responsibility</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">What We Do</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Secure the infrastructure and application</li>
              <li>Encrypt data in transit and at rest</li>
              <li>Monitor for security threats</li>
              <li>Maintain compliance standards</li>
              <li>Respond to security incidents</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">What You Can Do</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Use strong, unique passwords</li>
              <li>Keep your Salesforce org secure (apply security updates, review connected apps)</li>
              <li>Regularly review which users have access to your FlowDocs account</li>
              <li>Monitor the activity in your Salesforce Connected Apps OAuth Usage</li>
              <li>Report suspicious activity immediately to security@getflowdocs.com</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Vulnerability Disclosure</h2>
            <p className="text-gray-700 mb-3">We welcome security researchers to responsibly disclose vulnerabilities.</p>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Reporting Process</h3>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700 mb-4">
              <li>Email: <a href="mailto:security@getflowdocs.com" className="text-blue-600 hover:underline">security@getflowdocs.com</a></li>
              <li>Include detailed description and steps to reproduce</li>
              <li>Allow 90 days for fix before public disclosure</li>
              <li>We'll acknowledge receipt within 48 hours</li>
            </ol>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Safe Harbor</h3>
            <p className="text-gray-700 mb-3">We consider security research conducted in good faith:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Not pursued legally</li>
              <li>Eligible for acknowledgment</li>
              <li>Appreciated and valued</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Authentication Method</h2>
            <p className="text-gray-700 mb-3">
              FlowDocs uses OAuth 2.0 - the same secure authentication method used by Salesforce's own tools. This means:
            </p>
            <ul className="list-none space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✅</span>
                <span>No passwords are ever stored by FlowDocs</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✅</span>
                <span>You authorize access through Salesforce's login page</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✅</span>
                <span>Your credentials never pass through our servers</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✅</span>
                <span>You can revoke access at any time</span>
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Required Permissions</h2>
            <p className="text-gray-700 mb-4">FlowDocs requests the minimum OAuth scopes necessary:</p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scope</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purpose</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">What It Means</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">api</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Basic API access</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Allows FlowDocs to query your flow metadata</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">refresh_token</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Maintain connection</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Keeps you logged in between sessions</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">openid</td>
                    <td className="px-6 py-4 text-sm text-gray-700">User identification</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Identifies which user authorized access</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-900">
              <p className="text-sm">
                <strong>Note:</strong> While Salesforce labels the api scope as "Manage user data via APIs," 
                FlowDocs only performs read operations. This scope is required for any API access, even read-only.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What FlowDocs Can Access</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-green-600 mb-2 flex items-center">
                  <span className="text-green-500 mr-2">✅</span>
                  FlowDocs CAN read:
                </h3>
                <ul className="list-disc pl-8 space-y-1 text-gray-700">
                  <li>Flow definitions and metadata</li>
                  <li>Flow interview logs (for error analysis)</li>
                  <li>Basic user information (for "last modified by")</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-red-600 mb-2 flex items-center">
                  <span className="text-red-500 mr-2">❌</span>
                  FlowDocs CANNOT access:
                </h3>
                <ul className="list-disc pl-8 space-y-1 text-gray-700">
                  <li>Your customer data (Accounts, Contacts, Opportunities, etc.)</li>
                  <li>Custom object records</li>
                  <li>Personal information beyond basic user details</li>
                  <li>Any financial or sensitive business data</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Security Measures</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Code-Level Protection:</strong> Our application includes multiple safeguards to prevent any write operations</li>
              <li><strong>Security Event Logging:</strong> Critical security events (logins, org connections, auth failures) are logged with 90-day retention</li>
              <li><strong>Encrypted Connections:</strong> All data transfers use HTTPS encryption</li>
              <li><strong>No Data Storage:</strong> Customer data is never stored on our servers</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Monitor Access</h2>
            <p className="text-gray-700 mb-3">You can view and manage FlowDocs' access at any time:</p>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700">
              <li>Go to Setup → Apps → Connected Apps OAuth Usage</li>
              <li>Find "FlowDocs" in the list</li>
              <li>View access details and usage statistics</li>
              <li>Click "Revoke" to immediately remove access</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Revoke Access</h2>
            <p className="text-gray-700 mb-3">To disconnect FlowDocs from your org:</p>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Option 1: From Salesforce Setup</h3>
                <ol className="list-decimal pl-6 space-y-1 text-gray-700">
                  <li>Navigate to Setup → Apps → Connected Apps OAuth Usage</li>
                  <li>Find "FlowDocs"</li>
                  <li>Click "Revoke"</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Option 2: From FlowDocs</h3>
                <ol className="list-decimal pl-6 space-y-1 text-gray-700">
                  <li>Log into FlowDocs at <a href="https://getflowdocs.com" className="text-blue-600 hover:underline">getflowdocs.com</a></li>
                  <li>Go to the Orgs page</li>
                  <li>Click "Disconnect" next to your org</li>
                </ol>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">FAQ</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Q: Why does the permission say "Manage" if FlowDocs is read-only?</h3>
                <p className="text-gray-700">
                  A: This is Salesforce's standard wording for the api scope. Despite the label, FlowDocs only performs 
                  read operations. This same scope is used by all applications that need API access, even read-only ones.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Q: Can FlowDocs modify my flows?</h3>
                <p className="text-gray-700">
                  A: No. FlowDocs only reads flow metadata to generate documentation and analysis. We cannot and do not modify flows.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Q: What happens to my data?</h3>
                <p className="text-gray-700">
                  A: FlowDocs stores flow metadata (definitions, structure, AI-generated documentation) in our database until you delete your org or account.
                  Flow Interview logs are queried temporarily for error analysis but not stored. Your actual business data (customer records, financial data, etc.) never leaves Salesforce.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Q: What security standards does FlowDocs follow?</h3>
                <p className="text-gray-700">
                  A: FlowDocs leverages SOC 2 Type II certified infrastructure providers (Supabase, Vercel) and 
                  ISO 27001 certified cloud services. We follow security best practices including encrypted 
                  connections, audit logging, and minimal data retention.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Q: What security events are logged?</h3>
                <p className="text-gray-700">
                  A: We log critical security events including login attempts, Salesforce org connections/disconnections, 
                  and authentication failures. Logs are retained for 90 days for security monitoring and are automatically 
                  purged after that period to protect privacy.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact</h2>
            <p className="text-gray-700 mb-3">If you have security questions or concerns, please contact:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Email: <a href="mailto:security@getflowdocs.com" className="text-blue-600 hover:underline">security@getflowdocs.com</a></li>
              <li>Website: <a href="https://getflowdocs.com" className="text-blue-600 hover:underline">getflowdocs.com</a></li>
            </ul>
          </section>

        </article>
      </main>
      
      <Footer />
      <Chatbot />
    </div>
  );
}